import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

const PROTOCOL_VERSION = 1;
const IFRAME_SOURCE = 'bowtackle-iframe';
const HOST_SOURCE = 'bowtackle-host';
const READY_MESSAGE = 'navigation-ready';
const ACK_MESSAGE = 'navigation-ack';
const PUSH_MESSAGE = 'navigation-push';
const TRAVERSE_MESSAGE = 'navigation-traverse';
const DEFAULT_HANDSHAKE_INTERVAL = 1000;

const routeFromLocation = location =>
  `${location?.pathname || '/'}${location?.search || ''}${location?.hash || ''}`;

export const isSafeIframeRoute = route => {
  if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//')) {
    return false;
  }

  try {
    const base = 'https://bowtackle.invalid';
    return new URL(route, base).origin === base;
  } catch (e) {
    return false;
  }
};

const isEmbeddedWindow = windowObject => {
  try {
    return windowObject.self !== windowObject.top;
  } catch (e) {
    return true;
  }
};

const createIframeId = () =>
  `bowtackle-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;

/**
 * Connect React Router navigation inside the marketplace iframe to the dealer host's history.
 *
 * Until the host acknowledges protocol v1, the original browser history remains untouched. After
 * the ACK, logical pushes become local replaces and the host creates the real history entries. This
 * lets mobile browser Back/Forward gestures traverse the iframe journey without leaving the host.
 */
export const setupIframeNavigationBridge = ({
  history,
  windowObject,
  iframeId = createIframeId(),
  handshakeInterval = DEFAULT_HANDSHAKE_INTERVAL,
}) => {
  if (!history || !windowObject || !isEmbeddedWindow(windowObject)) {
    return () => {};
  }

  const parentWindow = windowObject.parent;
  const originalPush = history.push;
  const originalReplace = history.replace;
  let active = false;
  let depth = 0;
  let sequence = 0;
  let handshakeTimer;
  const routeEntries = new Map([
    [
      0,
      {
        route: routeFromLocation(history.location),
        state: history.location?.state,
      },
    ],
  ]);

  const postToHost = message => {
    parentWindow.postMessage(message, '*');
  };

  const postReady = () => {
    postToHost({
      source: IFRAME_SOURCE,
      type: READY_MESSAGE,
      version: PROTOCOL_VERSION,
      iframeId,
      route: routeFromLocation(history.location),
    });
  };

  const bridgedPush = (path, state) => {
    if (!active) {
      return originalPush.call(history, path, state);
    }

    originalReplace.call(history, path, state);
    depth += 1;
    sequence += 1;
    Array.from(routeEntries.keys()).forEach(entryDepth => {
      if (entryDepth >= depth) {
        routeEntries.delete(entryDepth);
      }
    });
    routeEntries.set(depth, {
      route: routeFromLocation(history.location),
      state: history.location?.state,
    });

    postToHost({
      source: IFRAME_SOURCE,
      type: PUSH_MESSAGE,
      version: PROTOCOL_VERSION,
      iframeId,
      route: routeFromLocation(history.location),
      depth,
      sequence,
    });

    return undefined;
  };

  const bridgedReplace = (path, state) => {
    const result = originalReplace.call(history, path, state);
    if (active) {
      routeEntries.set(depth, {
        route: routeFromLocation(history.location),
        state: history.location?.state,
      });
    }
    return result;
  };

  const handleMessage = event => {
    if (event.source !== parentWindow) {
      return;
    }

    const data = event.data;
    if (
      !data ||
      data.source !== HOST_SOURCE ||
      data.version !== PROTOCOL_VERSION ||
      data.iframeId !== iframeId
    ) {
      return;
    }

    if (data.type === ACK_MESSAGE) {
      if (!active) {
        active = true;
        depth = 0;
        sequence = 0;
        routeEntries.clear();
        routeEntries.set(0, {
          route: routeFromLocation(history.location),
          state: history.location?.state,
        });
        if (handshakeTimer) {
          windowObject.clearInterval(handshakeTimer);
          handshakeTimer = null;
        }
      }
      return;
    }

    if (
      data.type === TRAVERSE_MESSAGE &&
      active &&
      Number.isInteger(data.depth) &&
      data.depth >= 0 &&
      isSafeIframeRoute(data.route)
    ) {
      depth = data.depth;
      const localEntry = routeEntries.get(depth);
      const route = localEntry?.route || data.route;
      originalReplace.call(history, route, localEntry?.state);
    }
  };

  history.push = bridgedPush;
  history.replace = bridgedReplace;
  windowObject.addEventListener('message', handleMessage);
  postReady();
  handshakeTimer = windowObject.setInterval(() => {
    if (!active) {
      postReady();
    }
  }, handshakeInterval);

  return () => {
    if (handshakeTimer) {
      windowObject.clearInterval(handshakeTimer);
    }
    windowObject.removeEventListener('message', handleMessage);
    if (history.push === bridgedPush) {
      history.push = originalPush;
    }
    if (history.replace === bridgedReplace) {
      history.replace = originalReplace;
    }
  };
};

const IframeNavigationBridge = () => {
  const history = useHistory();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    return setupIframeNavigationBridge({ history, windowObject: window });
  }, [history]);

  return null;
};

export default IframeNavigationBridge;
